import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './RegisterPage.css';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    dietaryRestrictions: 'none',
    website: '', // Honeypot field - should remain empty
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Honeypot check - bots will fill this field
    if (formData.website) {
      setError('Registrering feilet. Vennligst prøv igjen senere.');
      return;
    }

    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Vennligst fyll ut alle feltene');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passordene matcher ikke');
      return;
    }

    if (formData.password.length < 6) {
      setError('Passordet må være minst 6 tegn');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Vennligst oppgi en gyldig e-postadresse');
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await new Promise<string>((resolve, reject) => {
        (window as any).grecaptcha.ready(() => {
          (window as any).grecaptcha
            .execute('6Lep0zssAAAAAPhln0rdGbwDaC0y61hnhctSvE5E', { action: 'register' })
            .then(resolve)
            .catch(reject);
        });
      });

      await register(
        formData.email,
        formData.password,
        formData.name,
        recaptchaToken,
        formData.dietaryRestrictions
      );
      navigate('/deltaker/mine-fester');
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Registrering feilet. Prøv igjen.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-card">
          <h1>Opprett konto</h1>
          <p className="register-subtitle">
            Registrer deg for å delta i Sykkelfest-arrangementer
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Fullt navn
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ola Nordmann"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                E-postadresse
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="ola@eksempel.no"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="dietaryRestrictions" className="form-label">
                Mat-allergier / kosthold
              </label>
              <select
                id="dietaryRestrictions"
                name="dietaryRestrictions"
                className="form-input"
                value={formData.dietaryRestrictions}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="none">Ingen</option>
                <option value="vegetarian">Vegetarianer</option>
                <option value="vegan">Veganer</option>
                <option value="gluten">Glutenallergi / cøliaki</option>
                <option value="lactose">Laktoseintoleranse</option>
                <option value="nuts">Nøtteallergi</option>
                <option value="shellfish">Skalldyrallergi</option>
                <option value="fish">Fiskallergi</option>
                <option value="eggs">Eggallergi</option>
                <option value="other">Annet (spesifiser i profil)</option>
              </select>
            </div>

            {/* Honeypot field - hidden from humans, visible to bots */}
            <div className="honeypot-field">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Passord
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minst 6 tegn"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Bekreft passord
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Skriv inn passord på nytt"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={loading}
            >
              {loading && (
                <span className="spinner" role="status" aria-label="Laster">
                  <svg className="spinner-icon" viewBox="0 0 50 50">
                    <circle
                      className="spinner-path"
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      strokeWidth="4"
                    ></circle>
                  </svg>
                </span>
              )}
              {loading ? 'Oppretter konto...' : 'Opprett konto'}
            </button>

            <p className="recaptcha-notice">
              Denne siden er beskyttet av reCAPTCHA og Googles{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                personvernerklæring
              </a>{' '}
              og{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                vilkår
              </a>{' '}
              gjelder.
            </p>
          </form>

          <div className="register-footer">
            <p>
              Har du allerede en konto?{' '}
              <Link to="/logg-inn" className="link">
                Logg inn her
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
